import { useState } from 'react';
import { Plug } from 'lucide-react';
import { updateLocation } from '@/lib/api';
import { Switch } from '@/components/ui/Switch';
import styles from './ResidentChargersCard.module.css';

/**
 * Per-Location resident-managed-chargers toggle. Phase 3 of the
 * tenant→location migration — replaces the role this card's
 * sibling `ResidentChargersCard` played at the tenant level.
 *
 * Distinct from `ResidentChargersCard` because:
 *   - target field: Location.allow_resident_managed_chargers
 *     (not Tenant.allow_resident_self_registration)
 *   - target endpoint: PUT /locations/:id (not PATCH
 *     /admin/tenants/:id/settings)
 *   - lifecycle: takes value + onChange via props from a parent
 *     that already owns the fetched Location doc, rather than
 *     fetching its own tenant-settings via useEffect on mount.
 *     The parent (LocationDetailPage) is the source of truth.
 *
 * Visual chrome reuses the existing ResidentChargersCard.module.css
 * intentionally — same on/off treatment, same copy framing,
 * same cardTitleRow layout. Phase 4 will delete the old card +
 * decide whether to keep the shared CSS module or inline.
 *
 * Optimistic UI: tap flips immediately. PUT runs in the
 * background; on error, the toggle reverts and an inline alert
 * surfaces. Admin app has no toast helper (verified during
 * Fix-C work — only the PWA's @/store/toast exists), so the
 * inline alert + console.error fallback are the recovery
 * surface.
 */
interface LocationResidentManagedCardProps {
  locationId: string;
  /** Current value, fed by the parent's LocationDetail state. */
  value: boolean;
  /** Notify the parent of a successful flip so its cached
   *  LocationDetail stays in sync — avoids a stale value
   *  re-rendering on a follow-up edit. */
  onChange?: (next: boolean) => void;
}

export function LocationResidentManagedCard({
  locationId, value, onChange,
}: LocationResidentManagedCardProps) {
  // Local mirror so the optimistic flip is rendered immediately
  // even before the parent's onChange callback fires (parent may
  // batch the update or be slow to re-render). Sync from props
  // when the parent's value changes by external means (refetch).
  const [localValue, setLocalValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Mirror props.value into local state on prop change. Avoids a
  // useEffect for the simple case — render-time sync of
  // remote-sourced state into a local optimistic-mirror is the
  // standard pattern.
  if (localValue !== value && !saving) {
    // Only resync when not in the middle of a save; otherwise the
    // mid-flight optimistic state would get clobbered by a stale
    // parent re-render.
    setLocalValue(value);
  }

  const handleToggle = async (next: boolean): Promise<void> => {
    if (saving) return;
    const previous = localValue;
    setLocalValue(next);
    setSaving(true);
    setError('');
    try {
      await updateLocation(locationId, {
        allow_resident_managed_chargers: next,
      });
      setSavedAt(Date.now());
      onChange?.(next);
    } catch (err) {
      // Roll back to the previous local state. Surface inline.
      // No toast helper in admin; console.error is the fallback
      // for ops debugging.
      setLocalValue(previous);
      const msg = err instanceof Error ? err.message : 'Failed to save';
      setError(msg);
      // eslint-disable-next-line no-console
      console.error('LocationResidentManagedCard update failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h2 className={styles.cardTitle}>
          <Plug size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Resident-Managed Chargers
        </h2>
      </div>

      {error && <div className={styles.alertErr}>{error}</div>}
      {savedAt && !error && (
        <div className={styles.alertOk}>Saved.</div>
      )}

      <div className={styles.residentToggleRow}>
        <div className={styles.residentToggleInfo}>
          <p className={styles.residentToggleLabel}>
            Allow residents to register their own chargers
          </p>
          <p className={styles.residentToggleHint}>
            When enabled, drivers can register their own charger
            at this building from the EVI app and configure who
            can use it. They manage access from the driver app,
            not the admin dashboard. When disabled, only
            operators can register chargers at this building.
          </p>
        </div>
        <Switch
          hideLabel
          label="Allow residents to register their own chargers"
          checked={localValue}
          disabled={saving}
          onChange={(e) => void handleToggle(e.target.checked)}
        />
      </div>
    </section>
  );
}
